import { useEffect, useState, useCallback } from 'react';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Loader2, Tag, ArrowLeft, GripVertical } from 'lucide-react';
import { Button } from '@foundation/components/ui/button';
import { Badge } from '@foundation/components/ui/badge';
import { useToast } from '@foundation/hooks/use-toast';
import { Label } from '@foundation/components/ui/label';
import type { ContextCategory, ContextQuestion } from '../../services/contextCategoryService';
import {
  fetchContextCategoriesAdmin,
  createCategory,
  updateCategory,
  deleteCategory,
  createQuestion,
  updateQuestion,
  deleteQuestion,
} from '../../services/contextCategoryService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@foundation/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@foundation/components/ui/dialog';
import type { QuestionFormData } from './components/QuestionForm';
import { QuestionForm } from './components/QuestionForm';

const INPUT_CLASS =
  'w-full px-3 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#33b864]/30 focus:border-[#33b864]/40 transition-all duration-200';

const INPUT_LABELS: Record<string, string> = {
  'yes-no': 'Yes / No',
  'yes-no-not-sure': 'Yes / No / Not Sure',
  'single-select': 'Single Select',
  'multi-select': 'Multi Select',
  'text': 'Text',
  'textarea': 'Textarea',
};

type View = 'list' | 'detail';

export const ContextCategoriesPage = () => {
  const { toast } = useToast();

  // ── View state ─────────────────────────────────────────────────────────────
  const [view, setView] = useState<View>('list');
  const [selectedCategory, setSelectedCategory] = useState<ContextCategory | null>(null);

  // ── Category list state ────────────────────────────────────────────────────
  const [categories, setCategories] = useState<ContextCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // ── New/Edit category dialog (list view) ───────────────────────────────────
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ContextCategory | null>(null);
  const [catName, setCatName] = useState('');
  const [catDescription, setCatDescription] = useState('');
  const [isSavingCat, setIsSavingCat] = useState(false);

  // ── Detail view: edit category fields ─────────────────────────────────────
  const [detailName, setDetailName] = useState('');
  const [detailDescription, setDetailDescription] = useState('');
  const [isSavingDetail, setIsSavingDetail] = useState(false);

  // ── Question dialog (detail view) ─────────────────────────────────────────
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<ContextQuestion | null>(null);

  // ── Delete dialogs ─────────────────────────────────────────────────────────
  const [deleteCatTarget, setDeleteCatTarget] = useState<ContextCategory | null>(null);
  const [deleteQTarget, setDeleteQTarget] = useState<{ id: string; text: string } | null>(null);

  // ── Load ───────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const cats = await fetchContextCategoriesAdmin();
      setCategories(cats);
      // Refresh selected category data if in detail view
      if (selectedCategory) {
        const updated = cats.find((c) => c.id === selectedCategory.id);
        if (updated) setSelectedCategory(updated);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load categories', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [toast, selectedCategory]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Open detail view for a category ───────────────────────────────────────
  const openDetail = (cat: ContextCategory) => {
    setSelectedCategory(cat);
    setDetailName(cat.name);
    setDetailDescription(cat.description ?? '');
    setView('detail');
  };

  const backToList = () => {
    setView('list');
    setSelectedCategory(null);
  };

  // ── Create category (then switch to detail) ────────────────────────────────
  const openNewCatDialog = () => {
    setEditingCategory(null);
    setCatName('');
    setCatDescription('');
    setCatDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!catName.trim()) return;
    setIsSavingCat(true);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, {
          name: catName.trim(),
          description: catDescription.trim() || undefined,
        });
        await load();
        toast({ title: 'Saved', description: 'Category updated.' });
        setCatDialogOpen(false);
      } else {
        const newCat = await createCategory({
          name: catName.trim(),
          description: catDescription.trim() || undefined,
          order_index: categories.length,
        });
        // Reload and open detail view for the new category
        const cats = await fetchContextCategoriesAdmin();
        setCategories(cats);
        const fresh = cats.find((c) => c.id === newCat.id) ?? newCat;
        setCatDialogOpen(false);
        openDetail(fresh);
        toast({ title: 'Created', description: 'Category created. Add questions below.' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to save category', variant: 'destructive' });
    } finally {
      setIsSavingCat(false);
    }
  };

  // ── Toggle active (list view) ──────────────────────────────────────────────
  const handleToggleActive = async (cat: ContextCategory) => {
    try {
      await updateCategory(cat.id, { is_active: !cat.isActive });
      setCategories((prev) => prev.map((c) => c.id === cat.id ? { ...c, isActive: !c.isActive } : c));
    } catch {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  // ── Delete category ────────────────────────────────────────────────────────
  const handleDeleteCategory = async () => {
    if (!deleteCatTarget) return;
    try {
      await deleteCategory(deleteCatTarget.id);
      setCategories((prev) => prev.filter((c) => c.id !== deleteCatTarget.id));
      if (selectedCategory?.id === deleteCatTarget.id) backToList();
      toast({ title: 'Deleted', description: 'Category deleted.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete category', variant: 'destructive' });
    } finally {
      setDeleteCatTarget(null);
    }
  };

  // ── Save category details (detail view inline edit) ────────────────────────
  const handleSaveDetail = async () => {
    if (!selectedCategory || !detailName.trim()) return;
    setIsSavingDetail(true);
    try {
      await updateCategory(selectedCategory.id, {
        name: detailName.trim(),
        description: detailDescription.trim() || undefined,
      });
      const updatedCat = { ...selectedCategory, name: detailName.trim(), description: detailDescription.trim() || null };
      setSelectedCategory(updatedCat);
      setCategories((prev) => prev.map((c) => c.id === updatedCat.id ? updatedCat : c));
      toast({ title: 'Saved', description: 'Category updated.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save category', variant: 'destructive' });
    } finally {
      setIsSavingDetail(false);
    }
  };

  // ── Questions (detail view) ────────────────────────────────────────────────
  const openNewQuestion = () => {
    setEditingQuestion(null);
    setQuestionDialogOpen(true);
  };

  const openEditQuestion = (q: ContextQuestion) => {
    setEditingQuestion(q);
    setQuestionDialogOpen(true);
  };

  const handleSaveQuestion = async (data: QuestionFormData) => {
    if (!selectedCategory) return;
    if (editingQuestion) {
      await updateQuestion(selectedCategory.id, editingQuestion.id, {
        text: data.text,
        input_type: data.input_type,
        options: data.options.length > 0 ? data.options : undefined,
        placeholder: data.placeholder || undefined,
        used_for: data.used_for || undefined,
        is_required: data.is_required,
      });
    } else {
      await createQuestion(selectedCategory.id, {
        text: data.text,
        input_type: data.input_type,
        options: data.options.length > 0 ? data.options : undefined,
        placeholder: data.placeholder || undefined,
        used_for: data.used_for || undefined,
        is_required: data.is_required,
        order_index: selectedCategory.questions.length,
      });
    }
    const cats = await fetchContextCategoriesAdmin();
    setCategories(cats);
    const updated = cats.find((c) => c.id === selectedCategory.id);
    if (updated) setSelectedCategory(updated);
    setQuestionDialogOpen(false);
    toast({ title: 'Saved', description: `Question ${editingQuestion ? 'updated' : 'added'}.` });
  };

  const handleDeleteQuestion = async () => {
    if (!deleteQTarget || !selectedCategory) return;
    try {
      await deleteQuestion(selectedCategory.id, deleteQTarget.id);
      const cats = await fetchContextCategoriesAdmin();
      setCategories(cats);
      const updated = cats.find((c) => c.id === selectedCategory.id);
      if (updated) setSelectedCategory(updated);
      toast({ title: 'Deleted', description: 'Question deleted.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to delete question', variant: 'destructive' });
    } finally {
      setDeleteQTarget(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render: LIST VIEW
  // ─────────────────────────────────────────────────────────────────────────

  if (view === 'list') {
    return (
      <div className="px-4 py-5 md:p-8">
        {/* Header */}
        <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#33b864]/10 border border-[#33b864]/20 text-[#33b864] text-xs font-medium mb-4">
              <Tag className="h-3.5 w-3.5" />
              Admin
            </div>
            <h1 className="text-2xl font-bold text-foreground">Context Categories</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-xl">
              Manage the categories shown in the Organisation Context page. Each category contains
              questions that, when completed, unlock the matching document library.
            </p>
          </div>
          <Button
            onClick={openNewCatDialog}
            className="bg-[#33b864] hover:bg-[#2da358] text-white border-0 gap-2 flex-shrink-0"
          >
            <Plus className="h-4 w-4" />
            New Category
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-[#33b864]" />
          </div>
        ) : categories.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground text-sm">
            No categories yet. Create one to get started.
          </div>
        ) : (
          <div className="bg-card border border-border rounded-2xl divide-y divide-border overflow-hidden shadow-sm">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-4 px-6 py-4 hover:bg-muted/20 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-foreground">{cat.name}</span>
                    <Badge
                      variant="outline"
                      className={cat.isActive
                        ? 'text-[#33b864] border-[#33b864]/30 bg-[#33b864]/8 text-[10px]'
                        : 'text-muted-foreground text-[10px]'}
                    >
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {cat.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-lg">{cat.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {cat.questions.length} question{cat.questions.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDetail(cat)}
                    className="gap-1.5 text-xs"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <button
                    type="button"
                    onClick={() => handleToggleActive(cat)}
                    className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors"
                    title={cat.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {cat.isActive
                      ? <ToggleRight className="h-4 w-4 text-[#33b864]" />
                      : <ToggleLeft className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteCatTarget(cat)}
                    className="p-2 rounded-lg hover:bg-destructive/10 text-destructive transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New/Edit category dialog */}
        <Dialog open={catDialogOpen} onOpenChange={setCatDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Name *</Label>
                <input
                  type="text"
                  value={catName}
                  onChange={(e) => setCatName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="e.g. Governance & Compliance"
                  autoFocus
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Description</Label>
                <textarea
                  value={catDescription}
                  onChange={(e) => setCatDescription(e.target.value)}
                  rows={2}
                  className={`${INPUT_CLASS} resize-none`}
                  placeholder="Optional description shown to org users"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCatDialogOpen(false)}>Cancel</Button>
                <Button
                  disabled={!catName.trim() || isSavingCat}
                  onClick={handleSaveCategory}
                  className="bg-[#33b864] hover:bg-[#2da358] text-white border-0"
                >
                  {isSavingCat ? 'Saving…' : editingCategory ? 'Save' : 'Create & Add Questions'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete category confirmation */}
        <AlertDialog open={!!deleteCatTarget} onOpenChange={(open: boolean) => !open && setDeleteCatTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete category?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete <strong>{deleteCatTarget?.name}</strong> and all its
                questions. Existing org answers referencing this category will become orphaned. This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteCategory}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render: DETAIL VIEW
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="px-4 py-5 md:p-8 max-w-3xl">
      {/* Back */}
      <button
        type="button"
        onClick={backToList}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Context Categories
      </button>

      {/* Category info (inline edit) */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm mb-6">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Category Details</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Name *</Label>
            <input
              type="text"
              value={detailName}
              onChange={(e) => setDetailName(e.target.value)}
              className={INPUT_CLASS}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Description</Label>
            <textarea
              value={detailDescription}
              onChange={(e) => setDetailDescription(e.target.value)}
              rows={2}
              className={`${INPUT_CLASS} resize-none`}
              placeholder="Optional description shown to org users"
            />
          </div>
          <div className="flex justify-end">
            <Button
              disabled={!detailName.trim() || isSavingDetail}
              onClick={handleSaveDetail}
              size="sm"
              className="bg-[#33b864] hover:bg-[#2da358] text-white border-0"
            >
              {isSavingDetail ? 'Saving…' : 'Save Details'}
            </Button>
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Questions</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Org users must answer all required questions to unlock documents in this category.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={openNewQuestion} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add Question
          </Button>
        </div>

        {!selectedCategory || selectedCategory.questions.length === 0 ? (
          <div className="px-6 py-8 text-sm text-muted-foreground italic text-center">
            No questions yet. Click "Add Question" to get started.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {selectedCategory.questions.map((q, idx) => (
              <div key={q.id} className="flex items-start gap-3 px-6 py-4 hover:bg-muted/20">
                <GripVertical className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    {idx + 1}. {q.text}
                    {q.isRequired && <span className="text-destructive ml-0.5">*</span>}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Type: {INPUT_LABELS[q.inputType] ?? q.inputType}
                    {q.options && q.options.length > 0 && ` · ${q.options.length} options`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openEditQuestion(q)}
                  className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors flex-shrink-0"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteQTarget({ id: q.id, text: q.text.slice(0, 60) })}
                  className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive transition-colors flex-shrink-0"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Question dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            <QuestionForm
              initial={editingQuestion ? {
                text: editingQuestion.text,
                input_type: editingQuestion.inputType,
                options: editingQuestion.options ?? [],
                placeholder: editingQuestion.placeholder ?? '',
                used_for: editingQuestion.usedFor ?? '',
                is_required: editingQuestion.isRequired,
              } : undefined}
              onSave={handleSaveQuestion}
              onCancel={() => setQuestionDialogOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete question confirmation */}
      <AlertDialog open={!!deleteQTarget} onOpenChange={(open: boolean) => !open && setDeleteQTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete question?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>"{deleteQTarget?.text}"</strong> will be permanently deleted. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteQuestion}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
